// Home view — onboarding banner, greeting, continue card, three doors,
// recent activity. Per spec §11.

import { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Sparkles,
  Zap,
  Palette,
  LayoutGrid,
  Pencil,
  ShoppingCart,
  X,
} from 'lucide-react';
import { listDesigns, reorderDesign, dispatchCartAdd, forkDesign, StudioApiError } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { useStudio } from '../../lib/store';
import { fmtRelative, fmtDate, fmtQty } from '../../lib/format';
import type { CustomerSummary, Design } from '../../lib/types';
import { SkeletonGrid } from '../../components/LoadingSkeleton';
import { ActivityFeed } from '../../components/ActivityFeed';
import { AttachedDesignBanner } from '../../components/AttachedDesignBanner';
import './home.css';

interface HomeViewProps {
  customer: CustomerSummary;
}

// Local storage key for the onboarding banner — once dismissed we don't
// re-show it for this browser. Per spec §11 the banner is a one-time
// orientation hint, not a recurring notification.
const ONBOARD_DISMISS_KEY = 'md-studio:onboard-dismissed-v1';

export function HomeView({ customer }: HomeViewProps) {
  const setView = useStudio((s) => s.setView);
  const pushToast = useStudio((s) => s.pushToast);
  const [showOnboard, setShowOnboard] = useState(false);

  useEffect(() => {
    try {
      setShowOnboard(window.localStorage.getItem(ONBOARD_DISMISS_KEY) !== '1');
    } catch {
      // localStorage blocked (private browsing) — show by default.
      setShowOnboard(true);
    }
  }, []);

  function dismissOnboard(): void {
    setShowOnboard(false);
    try {
      window.localStorage.setItem(ONBOARD_DISMISS_KEY, '1');
    } catch {
      /* noop */
    }
  }

  // Pull the customer's most-recently-edited design to feature on the
  // Continue Card.
  const continueQuery = useQuery({
    queryKey: qk.designs({ home: 'continue' }),
    queryFn: () => listDesigns({ sort: 'recently_edited', limit: 1 }),
  });
  const featured: Design | undefined = continueQuery.data?.items[0];

  // Counts for the "three doors" labels.
  const allQuery = useQuery({
    queryKey: qk.designs({ home: 'counts' }),
    queryFn: () => listDesigns({ sort: 'recently_ordered', limit: 1 }),
  });
  const totalDesigns = allQuery.data?.total ?? 0;

  const reorderMut = useMutation({
    mutationFn: async (id: string) => {
      const desc = await reorderDesign(id);
      return dispatchCartAdd(desc);
    },
    onSuccess: (url) => (window.location.href = url),
    onError: (err) => pushToast({ type: 'error', message: err instanceof StudioApiError ? err.message : 'Could not reorder' }),
  });

  const forkMut = useMutation({
    mutationFn: (id: string) => forkDesign(id),
    onSuccess: (resp) => (window.location.href = resp.editor_url),
    onError: () => pushToast({ type: 'error', message: 'Could not edit a copy' }),
  });

  const greetingName = customer.first_name || customer.email.split('@')[0] || customer.email;

  return (
    <section aria-label="Home">
      {/* Attached-design banner shows above the onboarding banner so an
          existing customer with a fresh staff-attached design notices it
          first. Renders nothing when the customer has no unviewed
          attached designs. */}
      <AttachedDesignBanner />
      {showOnboard && (
        <div className="onboard-banner" role="region" aria-label="Quick orientation">
          <div className="onboard-banner__left">
            <span className="onboard-banner__icon" aria-hidden="true">
              <Sparkles size={22} strokeWidth={1.6} />
            </span>
            <div>
              <h4>New here? Take a 60-second tour.</h4>
              <p>We&rsquo;ll show you where to find everything. You can always come back.</p>
            </div>
          </div>
          <div className="onboard-banner__actions">
            <button
              type="button"
              className="onboard-banner__cta"
              onClick={() => {
                // Phase B: tour content is on Help & Tutorials. Wire up a real
                // walkthrough later; for now navigate to the Help view so the
                // user lands somewhere intentional.
                setView('help');
              }}
            >
              Start tour
            </button>
            <button
              type="button"
              className="onboard-banner__close"
              aria-label="Dismiss"
              onClick={dismissOnboard}
            >
              <X size={16} strokeWidth={1.8} aria-hidden="true" />
            </button>
          </div>
        </div>
      )}

      <header className="view-head">
        <p className="eyebrow" style={{ marginBottom: 8 }}>
          Welcome back
        </p>
        <h1 className="serif-italic" style={{ marginBottom: 0 }}>
          Hi {capitalize(greetingName)}.
        </h1>
        <p className="sub" style={{ marginTop: 8 }}>
          Pick up where you left off, or start something new.
        </p>
      </header>

      {continueQuery.isLoading ? (
        <SkeletonGrid count={1} variant="reorder" />
      ) : featured ? (
        <article className="continue-card">
          <div className="continue-card__thumb">
            {featured.thumbnail.url ? (
              <img src={featured.thumbnail.url} alt={`Preview of ${featured.name ?? 'design'}`} />
            ) : null}
          </div>
          <div className="continue-card__body">
            <p className="eyebrow" style={{ color: 'var(--forest)', marginBottom: 12, fontWeight: 600 }}>
              You were just working on
            </p>
            <h2>{featured.name ?? 'Untitled design'}</h2>
            {featured.shopify.product_title && (
              <p style={{ color: 'var(--ink-soft)', marginTop: 6 }}>{featured.shopify.product_title}</p>
            )}
            <p className="continue-card__meta">
              Edited {fmtRelative(featured.updated_at)}
              {featured.rollups.total_orders > 0 && (
                <>
                  {' · '}Ordered {featured.rollups.total_orders} time{featured.rollups.total_orders === 1 ? '' : 's'}
                </>
              )}
              {featured.rollups.last_ordered_qty != null && (
                <>
                  {' · '}Last sent {fmtQty(featured.rollups.last_ordered_qty)} on {fmtDate(featured.rollups.last_ordered_at)}
                </>
              )}
            </p>
            <div className="continue-card__actions">
              <button
                type="button"
                className="btn btn--hero btn--lg"
                onClick={() => forkMut.mutate(featured.id)}
              >
                <Pencil size={16} aria-hidden="true" strokeWidth={1.6} />
                <span>Continue editing</span>
              </button>
              <button
                type="button"
                className="btn btn--secondary btn--lg"
                onClick={() => reorderMut.mutate(featured.id)}
              >
                <ShoppingCart size={16} aria-hidden="true" strokeWidth={1.6} />
                <span>
                  {/* Draft (never ordered) → "Order this design". Otherwise
                      "Reorder N as-is" with the prior qty if we have one. */}
                  {featured.status === 'draft'
                    ? 'Order this design'
                    : `Reorder${featured.rollups.last_ordered_qty != null ? ` ${fmtQty(featured.rollups.last_ordered_qty)}` : ''} as-is`}
                </span>
              </button>
            </div>
          </div>
        </article>
      ) : (
        <article className="onboarding-hero">
          <div className="onboarding-hero__icon" aria-hidden="true">
            <Sparkles size={28} strokeWidth={1.6} />
          </div>
          <h2>Welcome to Market Dwellings.</h2>
          <p>
            Let&rsquo;s start with your first design. Browse our templates and customize one in minutes —
            it&rsquo;ll land in your studio automatically.
          </p>
          <button
            type="button"
            className="btn btn--hero"
            onClick={() => {
              window.location.href = `https://${customer.shopify_shop_domain}/collections/all`;
            }}
          >
            Browse templates →
          </button>
        </article>
      )}

      <div className="three-doors">
        <DoorCard
          color="teal"
          icon={Zap}
          title="Reorder a favorite"
          body="Jump to your most-ordered designs. One click and it’s in your cart."
          cta="Go to Quick Reorder"
          onClick={() => setView('reorder')}
        />
        <DoorCard
          color="clay"
          icon={Palette}
          title="Start something new"
          body="Browse templates by product type and customize in minutes."
          cta="Browse all"
          onClick={() => {
            window.location.href = `https://${customer.shopify_shop_domain}/collections/all`;
          }}
        />
        <DoorCard
          color="sky"
          icon={LayoutGrid}
          title="Open my designs"
          body={`Every design you've ever made${totalDesigns > 0 ? ` (${totalDesigns})` : ''}. Edit a copy, reorder, organize.`}
          cta="Open library"
          onClick={() => setView('designs')}
        />
      </div>

      {/* Recent activity feed — only render when the customer has at least
          one design, otherwise the empty state from above is enough. */}
      {totalDesigns > 0 && (
        <div style={{ marginTop: 'var(--s-6)' }}>
          <ActivityFeed limit={10} />
        </div>
      )}
    </section>
  );
}

function DoorCard({
  color,
  icon: Icon,
  title,
  body,
  cta,
  onClick,
}: {
  color: 'teal' | 'clay' | 'sky';
  icon: typeof Zap;
  title: string;
  body: string;
  cta: string;
  onClick: () => void;
}) {
  return (
    <button type="button" className={`door door--${color}`} onClick={onClick}>
      <div className="door__icon" aria-hidden="true">
        <Icon size={24} strokeWidth={1.6} />
      </div>
      <h3>{title}</h3>
      <p>{body}</p>
      <span className="door__cta">{cta} →</span>
    </button>
  );
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
